import assessmentData from "@/data/fit2lift_uptime_assessment_v2.json";

export type AssessmentRoot = typeof assessmentData;

export type AssessmentSection = AssessmentRoot["sections"][number];

export type SectionQuestion = AssessmentSection["questions"][number];

export type Q10Block = AssessmentSection["q10"];

/** One screenable item: regular question row or participation Q10. */
export type FlatQuestion = (SectionQuestion | Q10Block) & { _isQ10?: boolean };

/**
 * The 10 capacity items for a section: the 9 question rows plus Q10.
 *
  * v2.1/v2.2 note — the Stiff and Sore items (`SS-WORST`, `SS-DAYS`, `SS-DESC`,
 * `SS-TRAJ`, `SS-SCREEN`) deliberately live at the root of the instrument under
 * `stiff_sore_scale`, NOT inside `section.questions`. That keeps them out of this
 * function by construction, so `sectionRawSumOrNull` can never add a 0-10 value
 * into the 0-50 capacity raw sum. The five Sleep Disturbance items (`SL-*`) sit
 * outside `sections` for the same reason. The capacity math is protected structurally
 * rather than by a filter a future edit could forget.
 */
export function flattenSectionQuestions(section: AssessmentSection): FlatQuestion[] {
  return [...section.questions, { ...section.q10, _isQ10: true as const }];
}

export function sectionRawSumOrNull(
  section: AssessmentSection,
  responses: Record<string, number>
): number | null {
  const qs = flattenSectionQuestions(section);
  let sum = 0;
  for (const q of qs) {
    const v = responses[q.id];
    if (v === undefined) return null;
    sum += v;
  }
  return sum;
}

export function getScaleAnchors(
  root: AssessmentRoot,
  q: SectionQuestion | Q10Block
): Record<string, string> {
  if ("scale_ref" in q && q.scale_ref) {
    const ref = q.scale_ref as keyof Pick<
      AssessmentRoot,
      "q8_scale" | "q9_scale" | "q10_scale"
    >;
    if (ref === "q8_scale") return root.q8_scale.anchors;
    if (ref === "q9_scale") return root.q9_scale.anchors;
    if (ref === "q10_scale") return root.q10_scale.anchors;
  }
  if ("custom_scale" in q && q.custom_scale && "scale_anchors" in q && q.scale_anchors) {
    return q.scale_anchors;
  }
  return root.scale_definition.anchors;
}

export function regionalCapacityPercent(sumRaw: number, maxRaw: number): number {
  if (maxRaw <= 0) return 100;
  const pct = 100 - (sumRaw / maxRaw) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

export function bandForPercent(
  pct: number,
  bands: AssessmentRoot["capacity_bands"]
): (typeof bands)[number] {
  const match = bands.find((b) => pct >= b.min && pct <= b.max);
  return match ?? bands[bands.length - 1]!;
}

export function overallCapacityPercent(regionals: number[]): number {
  if (regionals.length === 0) return 100;
  const sum = regionals.reduce((a, b) => a + b, 0);
  return Math.round(sum / regionals.length);
}

type JointSignalQ = SectionQuestion & {
  primary_joint_signal?: string[];
};

export function computePrimaryLimiter(
  section: AssessmentSection,
  selectedJoints: string[],
  responses: Record<string, number>
): { key: string; total: number; count: number }[] {
  const totals = new Map<string, { total: number; count: number }>();
  for (const key of selectedJoints) {
    totals.set(key, { total: 0, count: 0 });
  }
  const flat = flattenSectionQuestions(section);
  for (const q of flat) {
    const sig = (q as JointSignalQ).primary_joint_signal;
    if (!sig?.length) continue;
    const score = responses[q.id];
    if (score === undefined) continue;
    for (const joint of sig) {
      if (!selectedJoints.includes(joint)) continue;
      const cur = totals.get(joint) ?? { total: 0, count: 0 };
      cur.total += score;
      cur.count += 1;
      totals.set(joint, cur);
    }
  }
  return [...totals.entries()]
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => b.total - a.total || b.count - a.count);
}

export function formatJointLabel(section: AssessmentSection, key: string): string {
  const j = section.joints?.find((x) => x.key === key);
  return j?.label ?? key;
}

export type StiffSoreItemKey = keyof AssessmentRoot["stiff_sore_scale"];

/**
 * Respondent-facing text for one Stiff and Sore item in one section.
 *
 * Resolution order: per-section override, then the shared template with
 * `region_display` interpolated, then the raw region name as a last resort.
 *
 * `region_display` carries the words the workforce actually uses ("shoulders,
 * arms or hands", not "upper extremity"). Overrides exist because the headache
 * section is a symptom rather than a body area, so no noun substitution yields
 * a grammatical sentence ("...in your headache", "this area is").
 *
 * Respondent-facing text must always route through here. `section.region` is
 * the internal name and drives reports and storage; it is not what a worker
 * reads.
 */
export function stiffSoreTextFor(
  root: AssessmentRoot,
  section: AssessmentSection,
  key: StiffSoreItemKey
): string {
  const overrides = (section as { stiff_sore_overrides?: Record<string, string> })
    .stiff_sore_overrides;
  const override = overrides?.[key];
  if (override) return override;
  const display =
    (section as { region_display?: string }).region_display ??
    section.region.toLowerCase();
  return root.stiff_sore_scale[key].question_template.replace("[region]", display);
}

/** Convenience wrapper for the item that gates a whole section. */
export function screenerTextFor(
  root: AssessmentRoot,
  section: AssessmentSection
): string {
  return stiffSoreTextFor(root, section, "screener");
}

export type SleepModule = AssessmentRoot["sleep_module"];
export type SleepItem = SleepModule["items"][number];
export type SleepBandKey = SleepModule["bands"][number]["key"];

/**
 * Sleep Disturbance raw score, 0-25. Null until all five items are answered.
 *
 * Runs in the OPPOSITE direction to capacity: higher means more disturbed.
 * Deliberately not expressed as a percentage — capacity % is high-is-good, and
 * two opposing percentages on one report is how a safety director misreads a
 * number.
 *
 * The module lives at the root of the instrument, not inside `sections`, so it
 * is structurally impossible for a sleep answer to reach `sectionRawSumOrNull`
 * or the six-region capacity average.
 */
export function sleepRawScoreOrNull(
  root: AssessmentRoot,
  responses: Record<string, number>
): number | null {
  let sum = 0;
  for (const item of root.sleep_module.items) {
    const v = responses[item.id];
    if (v === undefined) return null;
    sum += v;
  }
  return sum;
}

export function sleepBandFor(root: AssessmentRoot, raw: number) {
  const clamped = Math.max(0, Math.min(root.sleep_module.max_raw, Math.round(raw)));
  return root.sleep_module.bands.find((b) => clamped >= b.min && clamped <= b.max)
    ?? root.sleep_module.bands[root.sleep_module.bands.length - 1]!;
}

/**
 * True when the respondent reported nodding off, or nearly, during the working
 * day. A workplace safety signal rather than a symptom: it surfaces for
 * referral like a neurological descriptor, and appears in an aggregate only
 * ever as a count.
 */
export function hasDaytimeSleepinessSafetyFlag(
  root: AssessmentRoot,
  responses: Record<string, number>
): boolean {
  for (const item of root.sleep_module.items) {
    const threshold = (item as { safety_flag_at?: number }).safety_flag_at;
    if (threshold === undefined) continue;
    const v = responses[item.id];
    if (v !== undefined && v >= threshold) return true;
  }
  return false;
}
